import type {
  BarometerSuccess,
} from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type { AuctionResumeEvent } from '@autobid/ui/types/websocket/AuctionResumeEvent'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import {
  getBarometerQueryKey,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import { BAROMETER_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useBarometerSocket'
import { updateNonStaleQueries } from '@autobid/ui/utils/updateNonStaleQueries'
import { useQueryClient } from '@tanstack/vue-query'

export function useBarometerAuctionResume() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()

  const auctionResume = () => {
    const callback = (
      event: AuctionResumeEvent,
      queryKey: ReturnType<typeof getBarometerQueryKey>,
    ) => {
      const { auctionId, resumeTime } = event.context
      updateNonStaleQueries(queryClient, queryKey, (
        item?: BarometerSuccess['data']['singleItem'],
      ): BarometerSuccess['data']['singleItem'] | undefined => {
        if (!item)
          return item

        if (item.item.itemData.auctionId !== auctionId)
          return item

        if (item.item.itemData.stage !== 'IN_AUCTION')
          return item

        const newTime = new Date(resumeTime * 1000).toISOString()

        return {
          ...item,
          item: {
            ...item.item,
            itemData: {
              ...item.item.itemData,
              pausedHotbidStartTime: null,
              auctionStartDate: newTime,
              hotbidStartTime: newTime,
            },
          },
        }
      })
    }

    startListen({
      eventName: 'premium.AUCTION_RESUME',
      callbackName: BAROMETER_SOCKET_CALLBACK,
      callback: (event) => {
        const currentCarKey = getBarometerQueryKey({
          type: 'current',
          auctionId: event.context.auctionId,
        })
        const barometersKey = getBarometerQueryKey({ type: 'item' })

        callback(event, currentCarKey)
        callback(event, barometersKey)
      },
    })
  }

  return { auctionResume }
}
